import { useState, useEffect, useMemo } from "react";
import SiderBar from "../../components/siderbarcomponents";
import {
  subscribeToServicios,
  subscribeToEmpleados,
  subscribeToCasas,
  addServicio,
  deleteServicio,
  updateServicioConAyudantes,
  getAyudantesNombres,
  getCasasServicio,
  subscribeToPagosDirectos,
  addPagoDirecto,
  deletePagoDirecto,
  updatePagoDirecto,
  Servicio,
  Empleado,
  Casa,
  PagoDirecto,
} from "../../firebase/firebaseService";
import {
  FaChevronLeft, FaChevronRight, FaPlus, FaTimes, FaTrash,
  FaClock, FaHome, FaUsers, FaDollarSign, FaCalendarAlt,
  FaEdit, FaCheck, FaSearch, FaUser,
} from "react-icons/fa";

// ─── Helpers ────────────────────────────────────────────────────────────────
const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const getQuincenaKey = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-${date.getDate() <= 15 ? "1" : "2"}`;
};
const getQuincenaLabel = (key: string) => {
  const [y, m, q] = key.split("-");
  return `${q === "1" ? "1–15" : "16–31"} de ${MESES[parseInt(m) - 1]} ${y}`;
};
const getPastQuincenas = (n: number) => {
  const list: string[] = [];
  let y = new Date().getFullYear(), mo = new Date().getMonth() + 1, q = new Date().getDate() <= 15 ? 1 : 2;
  for (let i = 0; i < n; i++) {
    if (q === 1) { q = 2; mo--; if (mo === 0) { mo = 12; y--; } } else q = 1;
    list.push(`${y}-${String(mo).padStart(2,"0")}-${q}`);
  }
  return list;
};
const formatDate  = (d: Date) => d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
const formatTime  = (d: Date) => d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
const calcHoras   = (i: Date, f: Date) => Math.max(0, Math.round(((f.getTime() - i.getTime()) / 3600000) * 100) / 100);
const toDateStr   = (d: Date) => { try { return d.toISOString().split("T")[0]; } catch { return ""; } };
const toTimeStr   = (d: Date) => `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;

// ─── Types ───────────────────────────────────────────────────────────────────
type EmpSel  = { id: string; nombre: string };
type CasaSel = { id: string; nombre: string; direccion?: string };

const emptyForm = {
  empleadosSeleccionados: [] as EmpSel[],
  casasSeleccionadas:     [] as CasaSel[], // create: múltiple / edit: solo 1
  fechaServicio: "",
  horaInicio: "08:00",
  horaFin: "10:00",
  precioServicio: "",
  notas: "",
};

// ─── EmployeePicker ──────────────────────────────────────────────────────────
function EmployeePicker({
  empleados, selected, onChange, onClose, title, singleSelect,
}: {
  empleados: Empleado[];
  selected: EmpSel[];
  onChange: (sel: EmpSel[]) => void;
  onClose: () => void;
  title?: string;
  singleSelect?: boolean;
}) {
  const [search, setSearch] = useState("");
  const toggle = (emp: Empleado) => {
    if (singleSelect) {
      // En modo single, siempre reemplazar con el empleado clickeado
      onChange([{ id: emp.id!, nombre: `${emp.nombre} ${emp.apellidos}` }]);
      onClose(); // Cerrar automáticamente después de seleccionar
    } else {
      // Modo multi-selección (original)
      const idx = selected.findIndex((s) => s.id === emp.id);
      if (idx >= 0) onChange(selected.filter((_, i) => i !== idx));
      else onChange([...selected, { id: emp.id!, nombre: `${emp.nombre} ${emp.apellidos}` }]);
    }
  };
  const filtered = empleados.filter(
    (e) => e.activo && `${e.nombre} ${e.apellidos}`.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{title || (singleSelect ? "Seleccionar Empleado" : "Seleccionar Equipo")}</h3>
            {!singleSelect && (
              <p className="text-[13px] text-gray-400 mt-0.5">
                El primero seleccionado será el <span className="font-semibold text-amber-500">empleado principal</span>
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><FaTimes size={15}/></button>
        </div>

        <div className="px-4 pt-3">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={13}/>
            <input type="text" placeholder="Buscar empleado..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-violet-400"/>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 gap-3">
          {filtered.map((emp) => {
            const selIdx = selected.findIndex((s) => s.id === emp.id);
            const isSel = selIdx >= 0;
            const isPrincipal = selIdx === 0;
            return (
              <div key={emp.id} onClick={() => toggle(emp)}
                className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  isSel ? "border-violet-400 bg-violet-50" : "border-gray-100 hover:border-violet-200 hover:bg-violet-50/30"
                }`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold mb-2 ${
                  isSel ? "bg-gradient-to-br from-violet-500 to-indigo-600" : "bg-gradient-to-br from-gray-300 to-gray-400"
                }`}>
                  {emp.nombre[0]}{emp.apellidos[0]}
                </div>
                <p className="text-[14px] font-semibold text-gray-800 leading-tight">{emp.nombre} {emp.apellidos}</p>
                {emp.costoPorHora > 0 && <p className="text-[12px] text-gray-400 mt-0.5">${emp.costoPorHora}/hr</p>}
                {isSel && (
                  <span className={`absolute top-2 right-2 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                    isPrincipal ? "bg-amber-400 text-white" : "bg-violet-500 text-white"
                  }`}>
                    {isPrincipal ? "Principal" : `Ayud. ${selIdx}`}
                  </span>
                )}
                {isSel && (
                  <span className="absolute top-2 left-2 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                    <FaCheck size={9} className="text-white"/>
                  </span>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-2 text-center text-gray-400 py-8 text-[14px]">Sin empleados activos</div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex gap-3">
          <button onClick={() => onChange([])} className="px-4 py-2 rounded-xl border border-gray-200 text-gray-500 text-[14px] font-semibold hover:bg-gray-50">
            Limpiar
          </button>
          <button onClick={onClose} disabled={selected.length === 0}
            className="flex-1 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold text-[15px] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-violet-200">
            Confirmar · {selected.length} empleado{selected.length !== 1 ? "s" : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── HousePicker ─────────────────────────────────────────────────────────────
function HousePicker({
  casas, selected, onChange, onClose, multiple = true, title,
}: {
  casas: Casa[];
  selected: CasaSel[];
  onChange: (sel: CasaSel[]) => void;
  onClose: () => void;
  multiple?: boolean;
  title?: string;
}) {
  const [search, setSearch] = useState("");
  const toggle = (casa: Casa) => {
    const isSel = selected.some((s) => s.id === casa.id);
    if (multiple) {
      if (isSel) onChange(selected.filter((s) => s.id !== casa.id));
      else onChange([...selected, { id: casa.id!, nombre: casa.nombreDueno, direccion: casa.direccion }]);
    } else {
      // single
      onChange([{ id: casa.id!, nombre: casa.nombreDueno, direccion: casa.direccion }]);
      onClose();
    }
  };
  const filtered = casas.filter(
    (c) => c.activa && `${c.nombreDueno} ${c.direccion ?? ""}`.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{title || "Seleccionar Casa(s)"}</h3>
            {multiple && (
              <p className="text-[13px] text-gray-400 mt-0.5">
                Si seleccionas varias, se creará <span className="font-semibold text-blue-500">un servicio por cada casa</span>
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><FaTimes size={15}/></button>
        </div>

        <div className="px-4 pt-3">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={13}/>
            <input type="text" placeholder="Buscar casa..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-400"/>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {filtered.map((casa) => {
            const isSel = selected.some((s) => s.id === casa.id);
            return (
              <div key={casa.id} onClick={() => toggle(casa)}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  isSel ? "border-blue-400 bg-blue-50" : "border-gray-100 hover:border-blue-200 hover:bg-blue-50/30"
                }`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${
                  isSel ? "bg-gradient-to-br from-blue-500 to-cyan-600" : "bg-gradient-to-br from-gray-300 to-gray-400"
                }`}>
                  {casa.nombreDueno.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-semibold text-gray-800">{casa.nombreDueno}</p>
                  {casa.direccion && <p className="text-[13px] text-gray-400 truncate">{casa.direccion}</p>}
                </div>
                {isSel && (
                  <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shrink-0">
                    <FaCheck size={10} className="text-white"/>
                  </span>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center text-gray-400 py-8 text-[14px]">Sin casas activas</div>
          )}
        </div>

        {multiple && (
          <div className="p-4 border-t border-gray-100 flex gap-3">
            <button onClick={() => onChange([])} className="px-4 py-2 rounded-xl border border-gray-200 text-gray-500 text-[14px] font-semibold hover:bg-gray-50">
              Limpiar
            </button>
            <button onClick={onClose} disabled={selected.length === 0}
              className="flex-1 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold text-[15px] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-blue-200">
              Confirmar · {selected.length} casa{selected.length !== 1 ? "s" : ""}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PagosAdmin() {
  const [servicios, setServicios] = useState<Servicio[]>([]);
  const [empleados, setEmpleados] = useState<Empleado[]>([]);
  const [casas,     setCasas]     = useState<Casa[]>([]);
  const [pagosDirectos, setPagosDirectos] = useState<PagoDirecto[]>([]);
  const [loading,   setLoading]   = useState(true);

  const [calYear,  setCalYear]  = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());

  // Create modal
  const [showModal,    setShowModal]    = useState(false);
  const [modalTab,     setModalTab]     = useState<"servicios" | "pagos">("servicios"); // Nueva pestaña activa
  const [saving,       setSaving]       = useState(false);
  const [form,         setForm]         = useState(emptyForm);
  const [errors,       setErrors]       = useState<Record<string, string>>({});
  const [showEmpPicker,  setShowEmpPicker]  = useState(false);
  const [showCasaPicker, setShowCasaPicker] = useState(false);

  // Formulario de pagos directos
  const [pagoDirectoForm, setPagoDirectoForm] = useState({
    empleadoSeleccionado: null as EmpSel | null,
    concepto: "",
    monto: "",
    fechaPago: new Date().toISOString().split("T")[0],
  });
  const [pagoDirectoErrors, setPagoDirectoErrors] = useState<Record<string, string>>({});
  const [showPagoDirectoEmpPicker, setShowPagoDirectoEmpPicker] = useState(false);

  // Edit modal
  const [editTarget,  setEditTarget]  = useState<Servicio | null>(null);
  const [editForm,    setEditForm]    = useState(emptyForm);
  const [editErrors,  setEditErrors]  = useState<Record<string, string>>({});
  const [updating,    setUpdating]    = useState(false);
  const [showEditEmpPicker,  setShowEditEmpPicker]  = useState(false);
  const [showEditCasaPicker, setShowEditCasaPicker] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<Servicio | null>(null);
  const [deletePagoDirectoTarget, setDeletePagoDirectoTarget] = useState<PagoDirecto | null>(null);

  // Edit pago directo
  const [editPagoDirectoTarget, setEditPagoDirectoTarget] = useState<PagoDirecto | null>(null);
  const [editPagoDirectoForm, setEditPagoDirectoForm] = useState({
    empleadoSeleccionado: null as EmpSel | null,
    concepto: "",
    monto: "",
    fechaPago: "",
  });
  const [showEditPagoDirectoEmpPicker, setShowEditPagoDirectoEmpPicker] = useState(false);

  const currentQ = getQuincenaKey(new Date());
  const [activeTab, setActiveTab] = useState(currentQ);
  const allTabs = [currentQ, ...getPastQuincenas(4)];

  useEffect(() => {
    const u1 = subscribeToServicios((d) => { setServicios(d); setLoading(false); });
    const u2 = subscribeToEmpleados(setEmpleados);
    const u3 = subscribeToCasas(setCasas);
    const u4 = subscribeToPagosDirectos(setPagosDirectos);
    return () => { u1(); u2(); u3(); u4(); };
  }, []);

  // ── Stats
  const now = new Date();
  const mesServicios = servicios.filter(
    (s) => s.fechaServicio.getFullYear() === now.getFullYear() && s.fechaServicio.getMonth() === now.getMonth()
  );
  const gananciasMes  = mesServicios.reduce((s, sv) => s + (sv.precioServicio || 0), 0);
  const totalHorasMes = mesServicios.reduce((s, sv) => s + sv.horasTrabajadas, 0);

  // ── Calendar
  const daysInMonth     = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(calYear, calMonth, 1).getDay();

  const serviciosForDay = useMemo(() => {
    const map: Record<number, Servicio[]> = {};
    servicios.forEach((s) => {
      if (s.fechaServicio.getFullYear() === calYear && s.fechaServicio.getMonth() === calMonth) {
        const d = s.fechaServicio.getDate();
        if (!map[d]) map[d] = [];
        map[d].push(s);
      }
    });
    return map;
  }, [servicios, calYear, calMonth]);

  const handleDayClick = (day: number) => {
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setForm({ ...emptyForm, fechaServicio: dateStr });
    setErrors({});
    setModalTab("servicios");
    setPagoDirectoForm({
      empleadoSeleccionado: null,
      concepto: "",
      monto: "",
      fechaPago: dateStr,
    });
    setPagoDirectoErrors({});
    setShowModal(true);
  };

  // ── Create
  const validateForm = (f: typeof emptyForm) => {
    const e: Record<string, string> = {};
    if (f.empleadosSeleccionados.length === 0) e.empleados = "Selecciona al menos un empleado";
    if (f.casasSeleccionadas.length === 0)     e.casas     = "Selecciona al menos una casa";
    if (!f.fechaServicio) e.fechaServicio = "La fecha es obligatoria";
    if (!f.horaInicio)    e.horaInicio   = "Hora inicio obligatoria";
    if (!f.horaFin)       e.horaFin      = "Hora fin obligatoria";
    return e;
  };

  const handleSubmit = async () => {
    const e = validateForm(form);
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const inicio        = new Date(`${form.fechaServicio}T${form.horaInicio}:00`);
      const fin           = new Date(`${form.fechaServicio}T${form.horaFin}:00`);
      const horas         = calcHoras(inicio, fin);
      const fechaServicio = new Date(form.fechaServicio + "T12:00:00");
      const principal     = form.empleadosSeleccionados[0];
      const ayudantes     = form.empleadosSeleccionados.slice(1);

      // Un solo servicio con todas las casas seleccionadas
      const primCasa = form.casasSeleccionadas[0];
      const payload: any = {
        empleadoPrincipalId:     principal.id,
        empleadoPrincipalNombre: principal.nombre,
        ayudantes,
        casaId:    primCasa.id,          // campo primario (backward compat)
        casaNombre: primCasa.nombre,
        casas:     form.casasSeleccionadas, // todas las casas
        fechaServicio,
        fechaHoraInicio: inicio,
        fechaHoraFin:    fin,
        horasTrabajadas: horas,
        quincena:        getQuincenaKey(fechaServicio),
      };
      if (form.precioServicio) payload.precioServicio = Number(form.precioServicio);
      if (form.notas.trim())   payload.notas          = form.notas.trim();
      await addServicio(payload);
      setShowModal(false);
      setForm(emptyForm);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // ── Pagos Directos
  const handlePagoDirectoSubmit = async () => {
    const e: Record<string, string> = {};
    if (!pagoDirectoForm.empleadoSeleccionado) e.empleado = "Seleccione un empleado";
    if (!pagoDirectoForm.concepto.trim())      e.concepto = "Ingrese el concepto del pago";
    if (!pagoDirectoForm.monto || Number(pagoDirectoForm.monto) <= 0) e.monto = "Ingrese un monto válido";
    if (Object.keys(e).length) { setPagoDirectoErrors(e); return; }

    setSaving(true);
    try {
      const fechaPago = new Date(pagoDirectoForm.fechaPago + "T12:00:00");
      await addPagoDirecto({
        empleadoId: pagoDirectoForm.empleadoSeleccionado!.id,
        empleadoNombre: pagoDirectoForm.empleadoSeleccionado!.nombre,
        concepto: pagoDirectoForm.concepto.trim(),
        monto: Number(pagoDirectoForm.monto),
        fechaPago,
        quincena: getQuincenaKey(fechaPago),
      });
      setShowModal(false);
      setPagoDirectoForm({
        empleadoSeleccionado: null,
        concepto: "",
        monto: "",
        fechaPago: new Date().toISOString().split("T")[0],
      });
      setPagoDirectoErrors({});
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // ── Edit
  const openEdit = (sv: Servicio) => {
    // Reconstruir equipo desde cualquier formato
    const equipo: EmpSel[] = [{ id: sv.empleadoPrincipalId, nombre: sv.empleadoPrincipalNombre }];
    if (sv.ayudantes?.length) {
      equipo.push(...sv.ayudantes);
    } else {
      if (sv.empleadoAyudanteId)  equipo.push({ id: sv.empleadoAyudanteId,  nombre: sv.empleadoAyudanteNombre  || "" });
      if (sv.empleadoAyudante2Id) equipo.push({ id: sv.empleadoAyudante2Id, nombre: sv.empleadoAyudante2Nombre || "" });
    }
    setEditTarget(sv);
    setEditForm({
      empleadosSeleccionados: equipo,
      casasSeleccionadas:     getCasasServicio(sv),
      fechaServicio:          toDateStr(sv.fechaServicio),
      horaInicio:             toTimeStr(sv.fechaHoraInicio),
      horaFin:                toTimeStr(sv.fechaHoraFin),
      precioServicio:         sv.precioServicio ? String(sv.precioServicio) : "",
      notas:                  sv.notas || "",
    });
    setEditErrors({});
  };

  const handleUpdate = async () => {
    const e = validateForm(editForm);
    if (Object.keys(e).length) { setEditErrors(e); return; }
    if (!editTarget?.id) return;
    setUpdating(true);
    try {
      const inicio        = new Date(`${editForm.fechaServicio}T${editForm.horaInicio}:00`);
      const fin           = new Date(`${editForm.fechaServicio}T${editForm.horaFin}:00`);
      const horas         = calcHoras(inicio, fin);
      const fechaServicio = new Date(editForm.fechaServicio + "T12:00:00");
      const principal  = editForm.empleadosSeleccionados[0];
      const primCasa   = editForm.casasSeleccionadas[0];

      const payload: any = {
        empleadoPrincipalId:     principal.id,
        empleadoPrincipalNombre: principal.nombre,
        ayudantes:               editForm.empleadosSeleccionados.slice(1),
        casaId:    primCasa.id,
        casaNombre: primCasa.nombre,
        casas:     editForm.casasSeleccionadas,
        fechaServicio,
        fechaHoraInicio: inicio,
        fechaHoraFin:    fin,
        horasTrabajadas: horas,
        quincena:        getQuincenaKey(fechaServicio),
      };
      if (editForm.precioServicio) payload.precioServicio = Number(editForm.precioServicio);
      if (editForm.notas.trim())   payload.notas          = editForm.notas.trim();

      // Borrar campos del formato legado al guardar con nuevo formato
      await updateServicioConAyudantes(editTarget.id, payload, true, true);
      setEditTarget(null);
    } catch (err) {
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try { await deleteServicio(deleteTarget.id); } finally { setDeleteTarget(null); }
  };

  const handleDeletePagoDirecto = async () => {
    if (!deletePagoDirectoTarget?.id) return;
    try { await deletePagoDirecto(deletePagoDirectoTarget.id); } finally { setDeletePagoDirectoTarget(null); }
  };

  // ── Edit Pago Directo
  const openEditPagoDirecto = (pd: PagoDirecto) => {
    setEditPagoDirectoTarget(pd);
    setEditPagoDirectoForm({
      empleadoSeleccionado: { id: pd.empleadoId, nombre: pd.empleadoNombre },
      concepto: pd.concepto,
      monto: String(pd.monto),
      fechaPago: toDateStr(pd.fechaPago),
    });
  };

  const handleUpdatePagoDirecto = async () => {
    const e: Record<string, string> = {};
    if (!editPagoDirectoForm.empleadoSeleccionado) e.empleado = "Seleccione un empleado";
    if (!editPagoDirectoForm.concepto.trim())      e.concepto = "Ingrese el concepto del pago";
    if (!editPagoDirectoForm.monto || Number(editPagoDirectoForm.monto) <= 0) e.monto = "Ingrese un monto válido";
    if (Object.keys(e).length) { setPagoDirectoErrors(e); return; }
    if (!editPagoDirectoTarget?.id) return;

    setUpdating(true);
    try {
      const fechaPago = new Date(editPagoDirectoForm.fechaPago + "T12:00:00");
      await updatePagoDirecto(editPagoDirectoTarget.id, {
        empleadoId: editPagoDirectoForm.empleadoSeleccionado!.id,
        empleadoNombre: editPagoDirectoForm.empleadoSeleccionado!.nombre,
        concepto: editPagoDirectoForm.concepto.trim(),
        monto: Number(editPagoDirectoForm.monto),
        fechaPago,
        quincena: getQuincenaKey(fechaPago),
      });
      setEditPagoDirectoTarget(null);
    } catch (err) {
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  // Combinar servicios y pagos directos en un solo array para la tabla
  type RegistroTabla = (Servicio & { tipo: 'servicio' }) | (PagoDirecto & { tipo: 'pagoDirecto' });

  const tabServicios = servicios.filter((s) => s.quincena === activeTab);
  const tabPagosDirectos = pagosDirectos.filter((p) => p.quincena === activeTab);

  // Combinar ambos tipos de registros
  const registrosTabla: RegistroTabla[] = [
    ...tabServicios.map(s => ({ ...s, tipo: 'servicio' as const })),
    ...tabPagosDirectos.map(p => ({ ...p, tipo: 'pagoDirecto' as const }))
  ].sort((a, b) => {
    const dateA = a.tipo === 'servicio' ? a.fechaServicio : a.fechaPago;
    const dateB = b.tipo === 'servicio' ? b.fechaServicio : b.fechaPago;
    return dateB.getTime() - dateA.getTime(); // Más reciente primero
  });

  const prevMonth = () => { if (calMonth === 0) { setCalYear(calYear - 1); setCalMonth(11); } else setCalMonth(calMonth - 1); };
  const nextMonth = () => { if (calMonth === 11) { setCalYear(calYear + 1); setCalMonth(0); } else setCalMonth(calMonth + 1); };

  // ─── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex-col lg:flex-row flex min-h-screen bg-slate-50">
      <SiderBar />
      <div className="flex-1 overflow-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-8 py-8">
          <h1 className="text-3xl font-bold text-white">Registro de Pagos</h1>
          <p className="text-blue-100 text-sm mt-1">Controla las horas y servicios de cada empleado</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            {[
              { icon: FaCalendarAlt, label: "Servicios este mes", value: mesServicios.length },
              { icon: FaDollarSign,  label: "Ingresos este mes",  value: `$${gananciasMes.toFixed(0)}` },
              { icon: FaClock,       label: "Horas trabajadas",   value: `${totalHorasMes.toFixed(1)}h` },
            ].map((s) => (
              <div key={s.label} className="bg-white/20 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
                  <s.icon className="text-white" size={16} />
                </div>
                <div>
                  <p className="text-blue-100 text-[13px] font-medium">{s.label}</p>
                  <p className="text-white text-2xl font-bold">{s.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 lg:p-8 space-y-8">

          {/* Calendar */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-gray-800">{MESES[calMonth]} {calYear}</h2>
                <div className="flex items-center gap-4 mt-2">
                  <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm bg-blue-100 border border-blue-200 inline-block"/>1ª quincena</span>
                  <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm bg-purple-100 border border-purple-200 inline-block"/>2ª quincena</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={prevMonth} className="p-2 rounded-xl hover:bg-gray-100 text-gray-500"><FaChevronLeft size={14}/></button>
                <button onClick={nextMonth} className="p-2 rounded-xl hover:bg-gray-100 text-gray-500"><FaChevronRight size={14}/></button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
              {["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"].map((d) => (
                <div key={d} className="text-center text-xs font-semibold text-gray-400 py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDayOfMonth }).map((_, i) => <div key={`e-${i}`}/>)}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                const isFirst = day <= 15;
                const svs = serviciosForDay[day] || [];
                const today = new Date();
                const isToday = today.getDate() === day && today.getMonth() === calMonth && today.getFullYear() === calYear;
                return (
                  <div key={day} onClick={() => handleDayClick(day)}
                    className={`relative flex flex-col p-2 rounded-xl cursor-pointer min-h-[64px] transition-all duration-150 hover:scale-[1.04] hover:shadow-md select-none ${
                      isFirst ? "bg-blue-50 hover:bg-blue-100 border border-blue-100" : "bg-purple-50 hover:bg-purple-100 border border-purple-100"
                    } ${isToday ? "ring-2 ring-offset-1 ring-blue-500" : ""}`}>
                    <span className={`text-xs font-bold ${isToday ? "text-blue-600" : "text-gray-600"}`}>{day}</span>
                    {svs.length > 0 && (
                      <span className={`mt-auto self-center text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                        isFirst ? "bg-blue-500 text-white" : "bg-purple-500 text-white"
                      }`}>{svs.length}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tabla por quincena */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex overflow-x-auto border-b border-gray-100 bg-gray-50">
              {allTabs.map((tab, idx) => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition-colors ${
                    activeTab === tab ? "bg-white text-blue-600 border-b-2 border-blue-500" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  }`}>
                  {idx === 0 ? "Quincena Actual" : getQuincenaLabel(tab)}
                  <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${activeTab === tab ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-500"}`}>
                    {servicios.filter((s) => s.quincena === tab).length + pagosDirectos.filter((p) => p.quincena === tab).length}
                  </span>
                </button>
              ))}
            </div>
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"/>
              </div>
            ) : registrosTabla.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                <FaCalendarAlt size={36} className="mb-3 text-gray-200"/>
                <p className="font-medium text-sm">Sin registros en esta quincena</p>
                <p className="text-xs mt-1">Haz clic en un día del calendario para agregar</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-6 py-3 text-[13px] font-semibold text-gray-500 uppercase">Tipo</th>
                      <th className="text-left px-6 py-3 text-[13px] font-semibold text-gray-500 uppercase">Fecha</th>
                      <th className="text-left px-6 py-3 text-[13px] font-semibold text-gray-500 uppercase">Empleado</th>
                      <th className="text-left px-6 py-3 text-[13px] font-semibold text-gray-500 uppercase hidden md:table-cell">Detalle</th>
                      <th className="text-left px-6 py-3 text-[13px] font-semibold text-gray-500 uppercase hidden sm:table-cell">Horas/Concepto</th>
                      <th className="text-left px-6 py-3 text-[13px] font-semibold text-gray-500 uppercase">Monto</th>
                      <th className="px-6 py-3"/>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {registrosTabla.map((registro) => {
                      if (registro.tipo === 'servicio') {
                        const sv = registro;
                        const ayudantes = getAyudantesNombres(sv);
                        return (
                          <tr key={`servicio-${sv.id}`} onClick={() => openEdit(sv)}
                            className="hover:bg-blue-50/30 transition-colors cursor-pointer group">
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center gap-1 text-[12px] font-bold px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                <FaHome size={9}/>Servicio
                              </span>
                            </td>
                            <td className="px-6 py-4 text-[15px] text-gray-600">{formatDate(sv.fechaServicio)}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                                  {sv.empleadoPrincipalNombre.slice(0,2).toUpperCase()}
                                </div>
                                <span className="text-[15px] font-medium text-gray-700">{sv.empleadoPrincipalNombre}</span>
                                <FaEdit size={11} className="text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity"/>
                              </div>
                            </td>
                            <td className="px-6 py-4 hidden md:table-cell">
                              <div className="space-y-1">
                                {getCasasServicio(sv).map((c, i) => (
                                  <span key={i} className="flex items-center gap-1 text-[13px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium w-fit">
                                    <FaHome size={9}/>{c.nombre}
                                  </span>
                                ))}
                                {ayudantes.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {ayudantes.map((n, i) => (
                                      <span key={i} className="text-[11px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{n}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-[13px] text-gray-500 hidden sm:table-cell">
                              <div className="flex items-center gap-1">
                                <FaClock size={10} className="text-gray-400"/>
                                <span className="font-semibold">{sv.horasTrabajadas}h</span>
                              </div>
                              <div className="text-[11px] text-gray-400 mt-0.5">
                                {formatTime(sv.fechaHoraInicio)} – {formatTime(sv.fechaHoraFin)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {sv.precioServicio
                                ? <span className="text-[15px] font-bold text-emerald-600">${sv.precioServicio.toFixed(2)}</span>
                                : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button onClick={(ev) => { ev.stopPropagation(); setDeleteTarget(sv); }}
                                className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                                <FaTrash size={12}/>
                              </button>
                            </td>
                          </tr>
                        );
                      } else {
                        // Pago Directo
                        const pd = registro;
                        return (
                          <tr key={`pago-${pd.id}`} onClick={() => openEditPagoDirecto(pd)}
                            className="hover:bg-amber-50/30 transition-colors cursor-pointer group">
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center gap-1 text-[12px] font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                                <FaDollarSign size={9}/>Pago Directo
                              </span>
                            </td>
                            <td className="px-6 py-4 text-[15px] text-gray-600">{formatDate(pd.fechaPago)}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                                  {pd.empleadoNombre.slice(0,2).toUpperCase()}
                                </div>
                                <span className="text-[15px] font-medium text-gray-700">{pd.empleadoNombre}</span>
                                <FaEdit size={11} className="text-amber-300 opacity-0 group-hover:opacity-100 transition-opacity"/>
                              </div>
                            </td>
                            <td className="px-6 py-4 hidden md:table-cell">
                              <span className="text-[13px] text-gray-600">{pd.concepto}</span>
                            </td>
                            <td className="px-6 py-4 text-[13px] text-gray-500 hidden sm:table-cell">
                              <span className="text-[11px] bg-amber-50 text-amber-700 px-2 py-1 rounded-full">Pago único</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-[15px] font-bold text-emerald-600">${pd.monto.toFixed(2)}</span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button onClick={(ev) => { ev.stopPropagation(); setDeletePagoDirectoTarget(pd); }}
                                className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                                <FaTrash size={12}/>
                              </button>
                            </td>
                          </tr>
                        );
                      }
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══ Modal: Crear Servicio ══════════════════════════════════════════════ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 pb-0">
              <div className="flex-1">
                <h2 className="text-lg font-bold text-gray-800">Registrar Pago / Servicio</h2>
                <p className="text-[13px] text-gray-400 mt-0.5">
                  {modalTab === "servicios"
                    ? (form.fechaServicio ? new Date(form.fechaServicio + "T12:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" }) : "Selecciona equipo, casa(s), fecha y horario")
                    : "Registra pago directo a empleado"}
                </p>
              </div>
              <button onClick={() => { setShowModal(false); setModalTab("servicios"); }} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><FaTimes size={16}/></button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 px-6 mt-4">
              <button
                onClick={() => setModalTab("servicios")}
                className={`flex-1 px-4 py-3 text-sm font-semibold transition-all ${
                  modalTab === "servicios"
                    ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaHome className="inline mr-2" size={12} />
                Servicios
              </button>
              <button
                onClick={() => setModalTab("pagos")}
                className={`flex-1 px-4 py-3 text-sm font-semibold transition-all ${
                  modalTab === "pagos"
                    ? "text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50/50"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaDollarSign className="inline mr-2" size={12} />
                Pagos Directos
              </button>
            </div>

            <div className="p-6 space-y-4">

              {/* ═══ TAB: SERVICIOS ═══ */}
              {modalTab === "servicios" && (<>
              {/* Picker de empleados */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5 flex items-center gap-1">
                  <FaUsers size={11}/> Equipo de trabajo *
                </label>
                <button onClick={() => setShowEmpPicker(true)}
                  className={`w-full px-4 py-3 border rounded-xl text-left transition-all hover:border-violet-400 ${
                    errors.empleados ? "border-red-400 bg-red-50" : form.empleadosSeleccionados.length > 0 ? "border-violet-400 bg-violet-50" : "border-gray-200"
                  }`}>
                  {form.empleadosSeleccionados.length === 0 ? (
                    <span className="text-[15px] text-gray-400">Haz clic para seleccionar empleados...</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {form.empleadosSeleccionados.map((emp, i) => (
                        <span key={emp.id} className={`text-[13px] font-semibold px-2.5 py-1 rounded-full ${
                          i === 0 ? "bg-amber-400 text-white" : "bg-violet-100 text-violet-700"
                        }`}>
                          {i === 0 ? "⭐ " : ""}{emp.nombre}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
                {errors.empleados && <p className="text-red-500 text-[13px] mt-1">{errors.empleados}</p>}
              </div>

              {/* Picker de casas */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5 flex items-center gap-1">
                  <FaHome size={11}/> Casa(s) *
                </label>
                <button onClick={() => setShowCasaPicker(true)}
                  className={`w-full px-4 py-3 border rounded-xl text-left transition-all hover:border-blue-400 ${
                    errors.casas ? "border-red-400 bg-red-50" : form.casasSeleccionadas.length > 0 ? "border-blue-400 bg-blue-50" : "border-gray-200"
                  }`}>
                  {form.casasSeleccionadas.length === 0 ? (
                    <span className="text-[15px] text-gray-400">Haz clic para seleccionar casa(s)...</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {form.casasSeleccionadas.map((casa) => (
                        <span key={casa.id} className="text-[13px] font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">
                          🏠 {casa.nombre}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
                {form.casasSeleccionadas.length > 1 && (
                  <p className="text-blue-500 text-[12px] mt-1 font-medium">
                    Se crearán {form.casasSeleccionadas.length} servicios (uno por cada casa)
                  </p>
                )}
                {errors.casas && <p className="text-red-500 text-[13px] mt-1">{errors.casas}</p>}
              </div>

              {/* Fecha */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Fecha del Servicio *</label>
                <input type="date" value={form.fechaServicio} onChange={(e) => setForm({ ...form, fechaServicio: e.target.value })}
                  className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.fechaServicio ? "border-red-400" : "border-gray-200"}`}/>
                {errors.fechaServicio && <p className="text-red-500 text-[13px] mt-1">{errors.fechaServicio}</p>}
              </div>

              {/* Horario */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Hora Inicio *</label>
                  <input type="time" value={form.horaInicio} onChange={(e) => setForm({ ...form, horaInicio: e.target.value })}
                    className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.horaInicio ? "border-red-400" : "border-gray-200"}`}/>
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Hora Fin *</label>
                  <input type="time" value={form.horaFin} onChange={(e) => setForm({ ...form, horaFin: e.target.value })}
                    className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.horaFin ? "border-red-400" : "border-gray-200"}`}/>
                </div>
              </div>

              {/* Duración */}
              {form.fechaServicio && form.horaInicio && form.horaFin && (
                <div className="bg-blue-50 rounded-xl px-4 py-3 flex items-center gap-2">
                  <FaClock className="text-blue-500" size={13}/>
                  <span className="text-[15px] text-blue-700 font-medium">
                    Duración: {calcHoras(
                      new Date(`${form.fechaServicio}T${form.horaInicio}:00`),
                      new Date(`${form.fechaServicio}T${form.horaFin}:00`)
                    ).toFixed(2)} horas
                  </span>
                </div>
              )}

              {/* Precio */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5"><FaDollarSign size={10} className="inline mr-1"/>Precio cobrado ($)</label>
                <input type="number" min="0" step="0.01" value={form.precioServicio} onChange={(e) => setForm({ ...form, precioServicio: e.target.value })} placeholder="0.00"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400"/>
              </div>

              {/* Notas */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Notas</label>
                <textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="Observaciones..." rows={2}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"/>
              </div>
              </>)}

              {/* ═══ TAB: PAGOS DIRECTOS ═══ */}
              {modalTab === "pagos" && (<>
              {/* Empleado */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5 flex items-center gap-1">
                  <FaUser size={11}/> Empleado *
                </label>
                <button onClick={() => setShowPagoDirectoEmpPicker(true)}
                  className={`w-full px-4 py-3 border rounded-xl text-left transition-all hover:border-violet-400 ${
                    pagoDirectoErrors.empleado ? "border-red-400 bg-red-50" : pagoDirectoForm.empleadoSeleccionado ? "border-violet-400 bg-violet-50" : "border-gray-200"
                  }`}>
                  {!pagoDirectoForm.empleadoSeleccionado ? (
                    <span className="text-[15px] text-gray-400">Haz clic para seleccionar empleado...</span>
                  ) : (
                    <span className="text-[13px] font-semibold px-2.5 py-1 rounded-full bg-violet-100 text-violet-700">
                      {pagoDirectoForm.empleadoSeleccionado.nombre}
                    </span>
                  )}
                </button>
                {pagoDirectoErrors.empleado && <p className="text-red-500 text-[13px] mt-1">{pagoDirectoErrors.empleado}</p>}
              </div>

              {/* Concepto */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Concepto / Descripción *</label>
                <textarea
                  value={pagoDirectoForm.concepto}
                  onChange={(e) => setPagoDirectoForm({ ...pagoDirectoForm, concepto: e.target.value })}
                  placeholder="Ej: Pago de nómina quincenal, contabilidad, etc..."
                  rows={3}
                  className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none ${
                    pagoDirectoErrors.concepto ? "border-red-400 bg-red-50" : "border-gray-200"
                  }`}
                />
                {pagoDirectoErrors.concepto && <p className="text-red-500 text-[13px] mt-1">{pagoDirectoErrors.concepto}</p>}
              </div>

              {/* Monto */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">
                  <FaDollarSign size={10} className="inline mr-1"/>Monto ($) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={pagoDirectoForm.monto}
                  onChange={(e) => setPagoDirectoForm({ ...pagoDirectoForm, monto: e.target.value })}
                  placeholder="0.00"
                  className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    pagoDirectoErrors.monto ? "border-red-400 bg-red-50" : "border-gray-200"
                  }`}
                />
                {pagoDirectoErrors.monto && <p className="text-red-500 text-[13px] mt-1">{pagoDirectoErrors.monto}</p>}
              </div>

              {/* Fecha */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Fecha del Pago *</label>
                <input
                  type="date"
                  value={pagoDirectoForm.fechaPago}
                  onChange={(e) => setPagoDirectoForm({ ...pagoDirectoForm, fechaPago: e.target.value })}
                  className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    pagoDirectoErrors.fechaPago ? "border-red-400 bg-red-50" : "border-gray-200"
                  }`}
                />
                {pagoDirectoErrors.fechaPago && <p className="text-red-500 text-[13px] mt-1">{pagoDirectoErrors.fechaPago}</p>}
              </div>
              </>)}

            </div>

            {/* Footer con botones */}
            <div className="flex gap-3 p-6 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[15px] hover:bg-gray-50">Cancelar</button>
              <button
                onClick={modalTab === "servicios" ? handleSubmit : handlePagoDirectoSubmit}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold text-[15px] hover:from-blue-700 hover:to-cyan-600 transition-all disabled:opacity-60 shadow-lg shadow-blue-200">
                {saving ? "Guardando..." : modalTab === "servicios" ? "Registrar Servicio" : "Registrar Pago"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Editar Servicio ════════════════════════════════════════════ */}
      {editTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <div className="flex items-center gap-2 mb-0.5"><FaEdit size={13} className="text-blue-500"/><span className="text-[13px] font-bold text-blue-500 uppercase tracking-wide">Editar servicio</span></div>
                <h2 className="text-lg font-bold text-gray-800">{editTarget.casaNombre}</h2>
                <p className="text-[13px] text-gray-400 mt-0.5">{formatDate(editTarget.fechaServicio)} · {editTarget.empleadoPrincipalNombre}</p>
              </div>
              <button onClick={() => setEditTarget(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><FaTimes size={16}/></button>
            </div>
            <div className="p-6 space-y-4">

              {/* Equipo */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5 flex items-center gap-1"><FaUsers size={11}/> Equipo de trabajo *</label>
                <button onClick={() => setShowEditEmpPicker(true)}
                  className={`w-full px-4 py-3 border rounded-xl text-left transition-all hover:border-violet-400 ${
                    editErrors.empleados ? "border-red-400" : editForm.empleadosSeleccionados.length > 0 ? "border-violet-400 bg-violet-50" : "border-gray-200"
                  }`}>
                  {editForm.empleadosSeleccionados.length === 0 ? (
                    <span className="text-[15px] text-gray-400">Seleccionar empleados...</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {editForm.empleadosSeleccionados.map((emp, i) => (
                        <span key={emp.id} className={`text-[13px] font-semibold px-2.5 py-1 rounded-full ${i === 0 ? "bg-amber-400 text-white" : "bg-violet-100 text-violet-700"}`}>
                          {i === 0 ? "⭐ " : ""}{emp.nombre}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
                {editErrors.empleados && <p className="text-red-500 text-[13px] mt-1">{editErrors.empleados}</p>}
              </div>

              {/* Casas (multi-select en edit) */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5 flex items-center gap-1"><FaHome size={11}/> Casa(s) *</label>
                <button onClick={() => setShowEditCasaPicker(true)}
                  className={`w-full px-4 py-3 border rounded-xl text-left transition-all hover:border-blue-400 ${
                    editErrors.casas ? "border-red-400" : editForm.casasSeleccionadas.length > 0 ? "border-blue-400 bg-blue-50" : "border-gray-200"
                  }`}>
                  {editForm.casasSeleccionadas.length === 0 ? (
                    <span className="text-[15px] text-gray-400">Seleccionar casa(s)...</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {editForm.casasSeleccionadas.map((c) => (
                        <span key={c.id} className="text-[13px] font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">🏠 {c.nombre}</span>
                      ))}
                    </div>
                  )}
                </button>
                {editErrors.casas && <p className="text-red-500 text-[13px] mt-1">{editErrors.casas}</p>}
              </div>

              {/* Fecha */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Fecha del Servicio *</label>
                <input type="date" value={editForm.fechaServicio} onChange={(e) => setEditForm({ ...editForm, fechaServicio: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400"/>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Hora Inicio *</label>
                  <input type="time" value={editForm.horaInicio} onChange={(e) => setEditForm({ ...editForm, horaInicio: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400"/>
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Hora Fin *</label>
                  <input type="time" value={editForm.horaFin} onChange={(e) => setEditForm({ ...editForm, horaFin: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400"/>
                </div>
              </div>

              {editForm.fechaServicio && editForm.horaInicio && editForm.horaFin && (
                <div className="bg-blue-50 rounded-xl px-4 py-3 flex items-center gap-2">
                  <FaClock className="text-blue-500" size={13}/>
                  <span className="text-[15px] text-blue-700 font-medium">
                    Duración: {calcHoras(
                      new Date(`${editForm.fechaServicio}T${editForm.horaInicio}:00`),
                      new Date(`${editForm.fechaServicio}T${editForm.horaFin}:00`)
                    ).toFixed(2)} horas
                  </span>
                </div>
              )}

              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5"><FaDollarSign size={10} className="inline mr-1"/>Precio cobrado ($)</label>
                <input type="number" min="0" step="0.01" value={editForm.precioServicio} onChange={(e) => setEditForm({ ...editForm, precioServicio: e.target.value })} placeholder="0.00"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400"/>
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Notas</label>
                <textarea value={editForm.notas} onChange={(e) => setEditForm({ ...editForm, notas: e.target.value })} rows={2}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"/>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-100">
              <button onClick={() => setEditTarget(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[15px] hover:bg-gray-50">Cancelar</button>
              <button onClick={handleUpdate} disabled={updating}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold text-[15px] disabled:opacity-60 shadow-lg shadow-blue-200 hover:from-blue-700 hover:to-cyan-600 transition-all">
                {updating ? "Actualizando..." : "Actualizar Servicio"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Editar Pago Directo ════════════════════════════════════════ */}
      {editPagoDirectoTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <div className="flex items-center gap-2 mb-0.5"><FaEdit size={13} className="text-amber-500"/><span className="text-[13px] font-bold text-amber-500 uppercase tracking-wide">Editar pago directo</span></div>
                <h2 className="text-lg font-bold text-gray-800">{editPagoDirectoTarget.empleadoNombre}</h2>
                <p className="text-[13px] text-gray-400 mt-0.5">{formatDate(editPagoDirectoTarget.fechaPago)} · ${editPagoDirectoTarget.monto.toFixed(2)}</p>
              </div>
              <button onClick={() => setEditPagoDirectoTarget(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><FaTimes size={16}/></button>
            </div>
            <div className="p-6 space-y-4">

              {/* Empleado */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5 flex items-center gap-1">
                  <FaUser size={11}/> Empleado *
                </label>
                <button onClick={() => setShowEditPagoDirectoEmpPicker(true)}
                  className={`w-full px-4 py-3 border rounded-xl text-left transition-all hover:border-violet-400 ${
                    pagoDirectoErrors.empleado ? "border-red-400 bg-red-50" : editPagoDirectoForm.empleadoSeleccionado ? "border-violet-400 bg-violet-50" : "border-gray-200"
                  }`}>
                  {!editPagoDirectoForm.empleadoSeleccionado ? (
                    <span className="text-[15px] text-gray-400">Haz clic para seleccionar empleado...</span>
                  ) : (
                    <span className="text-[13px] font-semibold px-2.5 py-1 rounded-full bg-violet-100 text-violet-700">
                      {editPagoDirectoForm.empleadoSeleccionado.nombre}
                    </span>
                  )}
                </button>
                {pagoDirectoErrors.empleado && <p className="text-red-500 text-[13px] mt-1">{pagoDirectoErrors.empleado}</p>}
              </div>

              {/* Concepto */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Concepto / Descripción *</label>
                <textarea
                  value={editPagoDirectoForm.concepto}
                  onChange={(e) => setEditPagoDirectoForm({ ...editPagoDirectoForm, concepto: e.target.value })}
                  placeholder="Ej: Pago de nómina quincenal, contabilidad, etc..."
                  rows={3}
                  className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none ${
                    pagoDirectoErrors.concepto ? "border-red-400 bg-red-50" : "border-gray-200"
                  }`}
                />
                {pagoDirectoErrors.concepto && <p className="text-red-500 text-[13px] mt-1">{pagoDirectoErrors.concepto}</p>}
              </div>

              {/* Monto */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">
                  <FaDollarSign size={10} className="inline mr-1"/>Monto ($) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editPagoDirectoForm.monto}
                  onChange={(e) => setEditPagoDirectoForm({ ...editPagoDirectoForm, monto: e.target.value })}
                  placeholder="0.00"
                  className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    pagoDirectoErrors.monto ? "border-red-400 bg-red-50" : "border-gray-200"
                  }`}
                />
                {pagoDirectoErrors.monto && <p className="text-red-500 text-[13px] mt-1">{pagoDirectoErrors.monto}</p>}
              </div>

              {/* Fecha */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Fecha del Pago *</label>
                <input
                  type="date"
                  value={editPagoDirectoForm.fechaPago}
                  onChange={(e) => setEditPagoDirectoForm({ ...editPagoDirectoForm, fechaPago: e.target.value })}
                  className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    pagoDirectoErrors.fechaPago ? "border-red-400 bg-red-50" : "border-gray-200"
                  }`}
                />
                {pagoDirectoErrors.fechaPago && <p className="text-red-500 text-[13px] mt-1">{pagoDirectoErrors.fechaPago}</p>}
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-100">
              <button onClick={() => setEditPagoDirectoTarget(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[15px] hover:bg-gray-50">Cancelar</button>
              <button onClick={handleUpdatePagoDirecto} disabled={updating}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-[15px] disabled:opacity-60 shadow-lg shadow-amber-200 hover:from-amber-600 hover:to-orange-600 transition-all">
                {updating ? "Actualizando..." : "Actualizar Pago"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Eliminar Servicio ═════════════════════════════════════════ */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><FaTrash size={20} className="text-red-500"/></div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">¿Eliminar servicio?</h3>
            <p className="text-gray-500 text-[15px] mb-6">Servicio del <strong>{formatDate(deleteTarget.fechaServicio)}</strong> en <strong>{deleteTarget.casaNombre}</strong>.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[15px] hover:bg-gray-50">Cancelar</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-[15px] hover:bg-red-600 shadow-lg shadow-red-200">Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Eliminar Pago Directo ═════════════════════════════════════ */}
      {deletePagoDirectoTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><FaTrash size={20} className="text-red-500"/></div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">¿Eliminar pago directo?</h3>
            <p className="text-gray-500 text-[15px] mb-6">Pago a <strong>{deletePagoDirectoTarget.empleadoNombre}</strong> del <strong>{formatDate(deletePagoDirectoTarget.fechaPago)}</strong> por <strong>${deletePagoDirectoTarget.monto.toFixed(2)}</strong>.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeletePagoDirectoTarget(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[15px] hover:bg-gray-50">Cancelar</button>
              <button onClick={handleDeletePagoDirecto} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-[15px] hover:bg-red-600 shadow-lg shadow-red-200">Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Pickers de CREATE ════════════════════════════════════════════════ */}
      {showEmpPicker && (
        <EmployeePicker
          empleados={empleados}
          selected={form.empleadosSeleccionados}
          onChange={(sel) => setForm({ ...form, empleadosSeleccionados: sel })}
          onClose={() => setShowEmpPicker(false)}
        />
      )}
      {showCasaPicker && (
        <HousePicker
          casas={casas}
          selected={form.casasSeleccionadas}
          onChange={(sel) => {
            const newForm = { ...form, casasSeleccionadas: sel };
            // Si hay una casa seleccionada y tiene precioHora, actualizar el precio del servicio
            if (sel.length > 0 && !form.precioServicio) {
              const casa = casas.find(c => c.id === sel[0].id);
              if (casa && (casa as any).precioHora > 0) {
                newForm.precioServicio = String((casa as any).precioHora);
              }
            }
            setForm(newForm);
          }}
          onClose={() => setShowCasaPicker(false)}
          multiple={true}
        />
      )}

      {/* ══ Picker de PAGOS DIRECTOS ═════════════════════════════════════════ */}
      {showPagoDirectoEmpPicker && (
        <EmployeePicker
          empleados={empleados}
          selected={pagoDirectoForm.empleadoSeleccionado ? [pagoDirectoForm.empleadoSeleccionado] : []}
          onChange={(sel) => setPagoDirectoForm({ ...pagoDirectoForm, empleadoSeleccionado: sel[0] || null })}
          onClose={() => setShowPagoDirectoEmpPicker(false)}
          title="Seleccionar Empleado"
          singleSelect={true}
        />
      )}

      {/* ══ Pickers de EDIT ══════════════════════════════════════════════════ */}
      {showEditEmpPicker && (
        <EmployeePicker
          empleados={empleados}
          selected={editForm.empleadosSeleccionados}
          onChange={(sel) => setEditForm({ ...editForm, empleadosSeleccionados: sel })}
          onClose={() => setShowEditEmpPicker(false)}
          title="Editar Equipo de Trabajo"
        />
      )}
      {showEditCasaPicker && (
        <HousePicker
          casas={casas}
          selected={editForm.casasSeleccionadas}
          onChange={(sel) => {
            const newForm = { ...editForm, casasSeleccionadas: sel };
            // Si hay una casa seleccionada y tiene precioHora, actualizar el precio del servicio
            if (sel.length > 0 && !editForm.precioServicio) {
              const casa = casas.find(c => c.id === sel[0].id);
              if (casa && (casa as any).precioHora > 0) {
                newForm.precioServicio = String((casa as any).precioHora);
              }
            }
            setEditForm(newForm);
          }}
          onClose={() => setShowEditCasaPicker(false)}
          multiple={true}
          title="Editar Casa(s)"
        />
      )}

      {/* ══ Picker de EDIT PAGO DIRECTO ══════════════════════════════════════ */}
      {showEditPagoDirectoEmpPicker && (
        <EmployeePicker
          empleados={empleados}
          selected={editPagoDirectoForm.empleadoSeleccionado ? [editPagoDirectoForm.empleadoSeleccionado] : []}
          onChange={(sel) => setEditPagoDirectoForm({ ...editPagoDirectoForm, empleadoSeleccionado: sel[0] || null })}
          onClose={() => setShowEditPagoDirectoEmpPicker(false)}
          title="Seleccionar Empleado"
          singleSelect={true}
        />
      )}
    </div>
  );
}
